/**
 * AH4 — "Sign in on another device": the QR a signed-in device shows to an unsigned one.
 *
 * Same feature as the workspace shell's dialog, on the surface that is served from the
 * authorization server's own origin — so here the session cookie already IS the principal and the
 * mint call needs nothing extra.
 *
 * What is on screen is a one-time LOGIN, and every decision follows from that: it expires while you
 * look at it (the countdown is the honest statement, and the code is removed when it runs out, not
 * left there looking usable); asking for a new one retires the old one server-side; the code is
 * drawn in this tab by the vendored encoder, so no third party ever holds the link; and copying is a
 * deliberate click that resets, so a login link does not sit in the clipboard.
 *
 * UX25 — the link is also a readonly text field, because a copy button is a promise a
 * browser is free to break (no clipboard permission, an http origin, a gesture it does
 * not recognise) and the text it replaced was ellipsised, i.e. unselectable. Now the
 * fallback is the obvious one: click the field, it selects itself, copy by hand.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import type { DeviceLink } from '../api/schemas';
import { qrSvg } from '../lib/qrcode';
import { Modal } from './Modal';

function mmss(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function DeviceLinkDialog({
  onClose,
  mint = () => api.createDeviceLink(),
}: {
  onClose: () => void;
  /** Injectable for tests (defaults to the real gateway call). */
  mint?: () => Promise<DeviceLink>;
}) {
  const [link, setLink] = useState<DeviceLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [left, setLeft] = useState(0);
  const [copied, setCopied] = useState(false);
  // A mint resolving after the dialog closed must not set state on a component that is gone.
  const generation = useRef(0);
  // The mint is read through a ref, never captured, because the DEFAULT is a fresh closure on every
  // render: capturing it made `request` a new function each render, which made the effect below a
  // new effect each render, which minted again — and each mint set state, which rendered again. One
  // open of this dialog fired ~115 codes a second until the gateway's per-user limit answered 429
  // to all of them and the QR never appeared at all. The ref breaks that cycle at its root: nothing
  // about a render can change the identity of `request`.
  const mintRef = useRef(mint);
  mintRef.current = mint;
  // One mint in flight at a time — a second click while the first is still open would otherwise
  // supersede the code the user is about to see (a new mint retires the old one server-side).
  const inFlight = useRef(false);

  const request = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const mine = ++generation.current;
    setLoading(true);
    setError(null);
    setCopied(false);
    try {
      const fresh = await mintRef.current();
      if (mine !== generation.current) return;
      setLink(fresh);
      setLeft(fresh.expires_in);
    } catch (e) {
      if (mine !== generation.current) return;
      setLink(null);
      const status = (e as { status?: number }).status;
      // 429 is a wait, not a failure: telling someone "try again" when the answer is "in a minute"
      // just sends them clicking.
      setError(
        status === 429
          ? 'Too many codes requested. Wait a moment and try again.'
          : 'Could not create a sign-in code.',
      );
    } finally {
      inFlight.current = false;
      if (mine === generation.current) setLoading(false);
    }
  }, []);

  // Exactly one mint per opening. A code is a credential the server has to keep and retire, so it
  // is minted on a deliberate act only: this open, or the "New code" button. Nothing else — not a
  // re-render, not the countdown tick, not the expiry, and not a 429, which is a wait rather than a
  // failure and so is never retried. The ref (not a dependency array) is what makes that hold under
  // StrictMode's double mount too, since refs survive it and effects do not.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void request();
  }, [request]);

  // The countdown runs off the CLIENT clock and is therefore approximate — a hint, not the rule.
  // The rule is the server's expiry, and a code redeemed after it is refused there.
  useEffect(() => {
    if (!link || left <= 0) return;
    const id = setInterval(() => setLeft((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(id);
  }, [link, left]);

  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  const expired = !!link && left <= 0;
  const svg = useMemo(() => {
    if (!link || expired) return null;
    try {
      return qrSvg(link.url, { size: 208 });
    } catch {
      // Too long to encode. The link below still works — a truncated code would be worse than none.
      return null;
    }
  }, [link, expired]);

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy the link.');
    }
  };

  return (
    <Modal title="Sign in on another device" onClose={onClose} width={320}>
      <div data-testid="device-link-dialog">
        <p className="muted" style={{ marginTop: 0 }}>
          Scan this code with the other device to sign in there — no password needed.
        </p>

        {error && <div className="error" data-testid="device-link-error">{error}</div>}

        {loading && !link && (
          <div data-testid="device-link-loading" className="muted" style={{ padding: '32px 0', textAlign: 'center' }}>
            Creating a code…
          </div>
        )}

        {link && !expired && (
          <>
            <div
              data-testid="device-link-qr"
              // Built in this tab from a string we constructed: the URL becomes a QR matrix, never
              // markup, so there is no untrusted input in this HTML.
              dangerouslySetInnerHTML={{ __html: svg || '' }}
              style={{
                display: 'flex', justifyContent: 'center', background: '#fff',
                borderRadius: 8, padding: 10, marginBottom: 10,
              }}
            />
            {/* UX25 — a readonly INPUT, not a div. The link used to be text with
                `text-overflow: ellipsis`, so when the copy button did not work — no
                clipboard permission, an http origin, a browser that refuses the API
                without a user gesture it recognises — there was no way to get the
                link out of the dialog at all: you cannot select what is ellipsised,
                and half a login URL is worse than none. A readonly field can be
                selected, dragged, right-click-copied, and read with a keyboard.
                Focusing it selects the whole thing, so "click, ⌘C" is the fallback. */}
            <input
              data-testid="device-link-url"
              className="input mono"
              readOnly
              value={link.url}
              title={link.url}
              aria-label="Sign-in link (select to copy)"
              spellCheck={false}
              onFocus={(e) => e.currentTarget.select()}
              onClick={(e) => e.currentTarget.select()}
              style={{ fontSize: 11, marginBottom: 10, width: '100%' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="btn btn-sm" data-testid="device-link-copy" onClick={() => void copy()}>
                {copied ? 'Copied' : 'Copy link'}
              </button>
              <span
                data-testid="device-link-countdown"
                title="This code stops working when the timer runs out."
                style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.7, marginLeft: 'auto' }}
              >{mmss(left)}</span>
            </div>
          </>
        )}

        {expired && (
          <div data-testid="device-link-expired" className="muted" style={{ padding: '28px 0', textAlign: 'center' }}>
            This code has expired.
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <button
            className="btn btn-sm btn-primary"
            data-testid="device-link-refresh"
            onClick={() => void request()}
            disabled={loading}
          >New code</button>
        </div>
      </div>
    </Modal>
  );
}
